// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Tab, { TabProps } from "@insite/mobius/Tab";
import TabGroup, { TabGroupStyleProps } from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import HeadDieChanges from "@store/Library/Components/Icons/HeadDieChanges";
import IndustryCertifications from "@store/Library/Components/Icons/IndustryCertifications";
import QualityControlledHeatTreatment from "@store/Library/Components/Icons/QualityControlledHeatTreatment";
import RecessDieChanges from "@store/Library/Components/Icons/RecessDieChanges";
import SatisfactionGurantee from "@store/Library/Components/Icons/SatisfactionGurantee";
import SpinCleanedScrews from "@store/Library/Components/Icons/SpinCleanedScrews";
import ThreadRollingDieChanges from "@store/Library/Components/Icons/ThreadRollingDieChanges";
import WireQuality from "@store/Library/Components/Icons/WireQuality";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css, FlattenSimpleInterpolation } from "styled-components";

const enum fields {
    widgetTabs = "widgetTabs",
    iconSize = "iconSize",
}

const icons = {
    threadRollingDieChanges: ThreadRollingDieChanges,
    recessDieChanges: RecessDieChanges,
    qualityControlledHeatTreatment: QualityControlledHeatTreatment,
    headDieChanges: HeadDieChanges,
    spinCleanedScrews: SpinCleanedScrews,
    industryCertifications: IndustryCertifications,
    wireQuality: WireQuality,
    satisfactionGurantee: SatisfactionGurantee,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.widgetTabs]: TabModel[];
        [fields.iconSize]: number;
    };
}

interface TabModel {
    fields: {
        heading: string;
        content: string;
        icon: string;
    };
}

type Props = OwnProps;

export interface DescriptionTabStyles {
    tabHeadings?: TabGroupStyleProps;
    tabContainer?: TypographyPresentationProps;
    tabContent?: TabGroupStyleProps;
    iconWrapper?: IconPresentationProps;
}

export const descriptionTabStyles: DescriptionTabStyles = {
    tabHeadings: {
        css: css`
            justify-content: space-around;
            overflow-x: unset;
            align-items: flex-end;
            word-wrap: normal;
            word-break: normal;
        `,
    },
    tabContent: {
        css: css`
            background-color: ${getColor("primary")};
            margin-top: -${pxToRem(1)};
            border: 0;
            padding: 4em;
        `,
    },
    tabContainer: {
        color: "primary.contrast",
    },
    iconWrapper: {
        css: css`
            position: relative;
            &:hover {
                g {
                    fill: ${getColor("primary")};
                }
            }
            span {
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            height: ${pxToRem(90)};
                            width: ${pxToRem(60)};
                            &::after {
                                bottom: -${pxToRem(35)} !important;
                            }
                            svg {
                                height: inherit;
                            }
                        `,
                        css`
                            height: ${pxToRem(150)};
                            width: ${pxToRem(100)};
                            &::after {
                                bottom: -${pxToRem(30)} !important;
                            }
                            svg {
                                height: inherit;
                            }
                        `,
                        css`
                            height: ${pxToRem(180)};
                            width: ${pxToRem(120)};
                            &::after {
                                bottom: -${pxToRem(25)} !important;
                            }
                            svg {
                                height: inherit;
                            }
                        `,
                    ]);
                }}
            }
        `,
    },
};

const tabGroup = descriptionTabStyles.tabHeadings?.css;
const tabContent = descriptionTabStyles.tabContent?.css;

const tabCss: FlattenSimpleInterpolation = css`
    text-transform: uppercase;
    font-weight: 700;
    font-size: 1.4em;
    &:hover {
        border-bottom: none;
    }
    &:focus {
        outline: rgb(0, 153, 255) solid ${pxToRem(2)};
    }
    g {
        fill: ${(props: TabProps) => (props.selected ? getColor("primary") : getColor("text.main"))};
    }
    span::after {
        content: "";
        position: absolute;
        border: ${pxToRem(20)} solid transparent;
        border-bottom: ${pxToRem(50)} solid
            ${(props: TabProps) => (props.selected ? getColor("primary") : "transparent")};
        bottom: -${pxToRem(20)};
        left: 50%;
        transform: translateX(-50%);
        transition: all ease 0.2s;
    }
    border-bottom: none;
    max-width: 25%;
    ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                font-size: 0.7em;
                font-weight: 300;
            `,
            css`
                font-size: 0.7em;
                font-weight: 300;
            `,
            css`
                font-size: 1.2em;
                font-weight: 500;
            `,
        ]);
    }}
` as FlattenSimpleInterpolation;

const GetIcon = (icon: string) => {
    return icons[icon as keyof typeof icons];
};

const DescriptionTabWidget: FC<Props> = ({ fields }: OwnProps) => {
    const tabs = fields.widgetTabs.map((field, index) => {
        const key = `${field.fields.heading}-${index}`;
        return (
            <Tab
                key={key}
                css={tabCss}
                headline={
                    <>
                        {field.fields.heading}
                        <StyledWrapper {...descriptionTabStyles.iconWrapper}>
                            <Icon src={GetIcon(field.fields.icon)} size={fields.iconSize} />
                        </StyledWrapper>
                    </>
                }
                tabKey={key}
            >
                <Typography {...descriptionTabStyles.tabContainer}>
                    {parse(field.fields.content, parserOptions)}
                </Typography>
            </Tab>
        );
    });

    return <TabGroup cssOverrides={{ tabContent, tabGroup }}>{tabs}</TabGroup>;
};

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const definition: WidgetDefinition = {
    group: "Basic",
    icon: "message-lines",
    fieldDefinitions: [
        {
            name: fields.widgetTabs,
            editorTemplate: "ListField",
            tab: contentTab,
            getDisplay: ({ fields: { heading } }) => heading,
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "heading",
                    displayName: "Heading Title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: "content",
                    displayName: "Content",
                    editorTemplate: "RichTextField",
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: "icon",
                    displayName: "Select Icon",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "ThreadRollingDieChanges", value: "threadRollingDieChanges" },
                        { displayName: "RecessDieChanges", value: "recessDieChanges" },
                        { displayName: "QualityControlledHeatTreatment", value: "qualityControlledHeatTreatment" },
                        { displayName: "HeadDieChanges", value: "headDieChanges" },
                        { displayName: "SpinCleanedScrews", value: "spinCleanedScrews" },
                        { displayName: "IndustryCertifications", value: "industryCertifications" },
                        { displayName: "WireQuality", value: "wireQuality" },
                        { displayName: "SatisfactionGurantee", value: "satisfactionGurantee" },
                    ],
                    defaultValue: "",
                },
            ],
        },
        {
            name: fields.iconSize,
            displayName: "Icon Size",
            editorTemplate: "IntegerField",
            defaultValue: 100,
            min: 1,
            fieldType: "General",
            tab: settingsTab,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: DescriptionTabWidget,
    definition,
};

export default widgetModule;
